import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  useMediaQuery,
  useTheme,
  Grid,
  Stack,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SpeedIcon from '@mui/icons-material/Speed';
import dayjs from 'dayjs';
import { encodeId } from '../utils/idEncoder';

type CarCardProps = {
  vehicle?: any;
  viewMode?: 'grid' | 'list';
};

const CarCard = ({ vehicle, viewMode = 'grid' }: CarCardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const effectiveViewMode = isMobile ? 'grid' : viewMode;
  const isList = effectiveViewMode === 'list';

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD',
      minimumFractionDigits: 0,
    }).format(price);

  const formatDate = (date?: string) =>
    date ? dayjs(date).format('DD MMM YYYY') : '';

  const getCleanDescription = (htmlContent: string) => {
    if (!htmlContent) return '';
    return htmlContent
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .trim();
  };

  const cleanDescription = getCleanDescription(vehicle?.description);

  // --- Sub-components ---
  const VehicleViewCountTag = ({ count }: { count: number }) => (
    <Box
      sx={{
        position: 'absolute',
        bottom: 8,
        left: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(4px)',
        color: '#fff',
        px: 1,
        py: 0.5,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 11,
        fontWeight: 600,
        zIndex: 1,
        border: '1px solid rgba(255,255,255,0.2)',
      }}
    >
      <VisibilityIcon sx={{ fontSize: 14, color: '#FFD700' }} />
      {count.toLocaleString()}
    </Box>
  );

  const VehicleSoldTag = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        right: 12,
        bgcolor: '#ef5350',
        color: '#fff',
        px: 1.5,
        py: 0.5,
        fontWeight: 800,
        fontSize: '0.7rem',
        borderRadius: '6px',
        zIndex: 2,
        boxShadow: '0 2px 8px rgba(239, 83, 80, 0.4)',
        letterSpacing: 0.5,
      }}
    >
      SOLD
    </Box>
  );

  if (!vehicle) return null;

  return (
    <Card
      component={RouterLink}
      to={`/vehicle-details/${encodeId(vehicle.vehicleId)}`}
      sx={{
        display: 'flex',
        flexDirection: isList ? 'row' : 'column',
        width: '100%',
        minHeight: isList ? 200 : 'auto',
        height: isList ? 'auto' : '100%',
        borderRadius: '16px',
        border: 'none',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        position: 'relative',
        textDecoration: 'none',
        overflow: 'hidden',
        color: 'black',
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
        bgcolor: '#fff',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 12px 30px rgba(0, 0, 0, 0.12)',
          '& .vehicle-img': { transform: 'scale(1.05)' },
        },
      }}
    >
      {vehicle?.statusId === 'VSL' && <VehicleSoldTag />}

      {/* --- IMAGE SECTION --- */}
      <Box
        sx={{
          position: 'relative',
          width: isList ? 260 : '100%',
          minWidth: isList ? 260 : 'auto',
          height: isList ? 'auto' : 'auto',
          minHeight: isList ? 200 : 'auto',
          aspectRatio: isList ? 'auto' : '4 / 3',
          backgroundColor: '#fff',
          flexShrink: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CardMedia
          component="img"
          className="vehicle-img"
          image={
            vehicle?.vehicleImageUrl ||
            'https://via.placeholder.com/600x400?text=No+Image'
          }
          alt={vehicle?.vehicleModel || 'Vehicle'}
          loading="lazy"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: isList ? 'cover' : 'contain',
            objectPosition: 'center',
            transition: 'transform 0.5s ease',
          }}
        />

        {vehicle?.racingAd && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              color: '#fff',
              fontWeight: 800,
              fontSize: '0.65rem',
              px: 1.2,
              py: 0.4,
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(255, 165, 0, 0.4)',
              zIndex: 2,
              textTransform: 'uppercase',
            }}
          >
            Premium
          </Box>
        )}

        {vehicle?.viewCount != null && (
          <VehicleViewCountTag count={vehicle.viewCount} />
        )}
      </Box>

      {/* --- CONTENT SECTION --- */}
      <CardContent
        sx={{
          flex: 1,
          p: isList ? 2.5 : 2,
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': { pb: isList ? 2.5 : 2 },
          minWidth: 0,
        }}
      >
        <Stack spacing={1} sx={{ height: '100%' }}>
          {/* ================= GRID MODE ================= */}
          {!isList && (
            <>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                {formatPrice(vehicle.askingPrice)}
              </Typography>

              <Typography
                variant="body1"
                fontWeight={700}
                color="text.primary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.3,
                  height: '2.6em',
                }}
              >
                {vehicle.vehicleBrand} {vehicle.vehicleModelName}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                sx={{ mt: 'auto', mb: 1 }}
              >
                {[
                  vehicle.firstRegistrationDate
                    ? dayjs(vehicle.firstRegistrationDate).format('YYYY')
                    : null,
                  vehicle.mileage
                    ? `${(vehicle.mileage / 1000).toFixed(0)}k km`
                    : null,
                  vehicle.vehicleBodyType,
                ]
                  .filter(Boolean)
                  .slice(0, 3)
                  .map((tag, i) => (
                    <Box
                      key={i}
                      sx={{
                        bgcolor: '#f0f2f5',
                        color: '#555',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        px: 1,
                        py: 0.4,
                        borderRadius: '6px',
                      }}
                    >
                      {tag}
                    </Box>
                  ))}
              </Stack>

              <Divider sx={{ borderColor: '#f0f0f0' }} />

              {vehicle?.showDealerInVehicleList && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="#FFA100"
                    noWrap
                  >
                    {vehicle.dealerName}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* ================= LIST MODE ================= */}
          {isList && (
            <>
              <Grid
                container
                justifyContent="space-between"
                alignItems="flex-start"
                wrap="nowrap"
              >
                {/* --- ENHANCED HEADER SECTION --- */}
                <Grid size={{ xs: 8 }} sx={{ minWidth: 0 }}>

                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{
                      transition: 'color 0.2s',
                      cursor: 'pointer',
                      lineHeight: 1.2,
                      textTransform: 'uppercase', // ✅ Forces text to be ALL CAPS
                      '&:hover': { color: 'text.secondary' },
                    }}
                  >
                    {vehicle.vehicleBrand} {vehicle.vehicleModelName}
                  </Typography>

                  {/* 2. Date is now a subtitle with icon */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ mt: 0.5, color: 'text.secondary' }}
                  >
                    <CalendarTodayIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate(vehicle.firstRegistrationDate)}
                    </Typography>
                  </Stack>
                </Grid>

                {/* Price Section */}
                <Grid size={{ xs: 4 }} textAlign="right" sx={{ flexShrink: 0 }}>
                  <Typography variant="h5" fontWeight={800} color="#2c3e50">
                    {formatPrice(vehicle.askingPrice)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 0.5 }} />

              {/* Specifications Grid */}
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                {[
                  {
                    label: 'Transmission',
                    value: vehicle.vehicleTransmissionType,
                  },
                  {
                    label: 'Fuel Type',
                    value: vehicle.vehicleFuelType,
                  },
                  {
                    label: 'Mileage',
                    value: vehicle.mileage
                      ? `${vehicle.mileage.toLocaleString()} km`
                      : '-',
                  },
                  {
                    label: 'Owners',
                    value: vehicle.noOfOwners ?? '-',
                  },
                ].map((item) => (
                  <Grid size={{ xs: 3 }} key={item.label}>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.primary"
                      noWrap
                    >
                      {item.value || '-'}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Description */}
              {cleanDescription ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    fontStyle: 'italic',
                    fontSize: '0.85rem',
                  }}
                >
                  {cleanDescription}
                </Typography>
              ) : null}

              <Box
                sx={{
                  mt: 'auto',
                  pt: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                {vehicle?.showDealerInVehicleList && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="#FFA100"
                    >
                      {vehicle.dealerName}
                    </Typography>
                  </Box>
                )}
                <Typography variant="caption" color="text.disabled">
                  Posted: {dayjs(vehicle.addDate).format('DD MMM YYYY')}
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CarCard;